import { DatasourceSetting } from '.';
import { JsonObject } from '../../helpers';
import { ORG_DEFAULT_AGENT_ID } from '@/lib/services/shortcut.service';
import { AgentModel } from '../agent.model';
import { PrivateModelSettings } from '@/lib/services/model.service';
import { DatasourceModel } from '../datasource.model';

export class ThreadSettingsModel {
  agentId!: string;
  privateModelId: string;
  datasources: DatasourceSetting[];
  loaded: boolean;

  constructor(props: JsonObject = {}) {
    const data = props as Record<keyof ThreadSettingsModel, unknown>;

    this.privateModelId = (data.privateModelId as string) || '';
    this.datasources = (data.datasources as DatasourceSetting[]) || [];
    this.agentId = (data.agentId as string) || ORG_DEFAULT_AGENT_ID;
    this.loaded = false;
  }

  toggleDatasource(datasourceId: string | string[], enabled: boolean) {
    const datasourceIds = Array.isArray(datasourceId) ? datasourceId : [datasourceId];

    datasourceIds.forEach((datasourceId) => {
      const dsIndex = this.datasources.findIndex(({ id }) => id === datasourceId);

      if (dsIndex > -1) {
        this.datasources[dsIndex].enabled = enabled;
      }
    });
  }

  togglePublicSearch(enabled: boolean) {
    this.datasources = this.datasources.map((datasource) => {
      if (datasource.isPublicInternet) {
        datasource.enabled = enabled;
      }

      return datasource;
    });
  }

  loadAndValidate(
    settings: ThreadSettingsModel,
    agents: AgentModel[],
    privateModelSettings: PrivateModelSettings,
    datasources: DatasourceModel[]
  ) {
    const { agentId, privateModelId, datasources: defaultDatasources } = settings;

    // validate/set the agent id
    const validAgent = agentId ? agents.find(({ id }) => id === agentId) : null;
    if (validAgent?.id) {
      this.agentId = validAgent.id;
    }

    // validate/set user's private model setting
    if (privateModelId) {
      const validPrivateModel = privateModelSettings.availablePrivateModels.find(
        ({ id }) => id === privateModelId
      );
      if (validPrivateModel) {
        this.privateModelId = validPrivateModel.id;
      }
    }

    // above did not set a valid value? set the default as defined by policy
    if (!this.privateModelId && privateModelSettings.defaultPrivateModelId) {
      const validPrivateModel = privateModelSettings.availablePrivateModels.find(
        ({ id }) => id === privateModelSettings.defaultPrivateModelId
      );

      if (validPrivateModel) {
        this.privateModelId = validPrivateModel.id;
      }
    }

    // set datasource settings, taking into account existing settings and defaults
    const validDatasources: DatasourceSetting[] = [];

    datasources.forEach((datasourceModel) => {
      const { id: datasourceId, isPublicInternet } = datasourceModel;
      let enabled = true;

      const existSetting = this.datasources.find(({ id }) => id === datasourceId);

      if (existSetting) {
        enabled = existSetting.enabled;
      } else {
        const existDefault = defaultDatasources.find(({ id }) => id === datasourceId);

        if (existDefault) {
          enabled = !!existDefault.enabled;
        } else if (isPublicInternet) {
          // these datasources are not enabled by default while we are not yet in love with public internet search
          enabled = false;
        }
      }

      validDatasources.push({
        id: datasourceId,
        isPublicInternet,
        enabled,
      });
    });

    this.datasources = validDatasources;

    this.loaded = true;
  }

  get publicSearchEnabled() {
    return this.datasources.some(({ isPublicInternet, enabled }) => isPublicInternet && enabled);
  }

  get isDefaultAgent(): boolean {
    return this.agentId === ORG_DEFAULT_AGENT_ID;
  }

  get _props(): { agentId: string; privateModelId: string; datasources: DatasourceSetting[] } {
    return {
      agentId: this.agentId,
      privateModelId: this.privateModelId,
      datasources: this.datasources,
    };
  }
}
